import { useState } from "react";
import { Play, Loader2, ChevronDown, ChevronRight } from "lucide-react";

const KW = /\b(import|from|as|def|return|for|in|if|else|await|async|print|True|False|None|with|lambda)\b/;
const TOKENS = /("[^"]*"|'[^']*'|#.*$|\b\d+(?:\.\d+)?\b|\b(?:import|from|as|def|return|for|in|if|else|await|async|print|True|False|None|with|lambda)\b)/gm;

const highlight = (code) =>
  code.split(TOKENS).map((t, i) => {
    if (!t) return null;
    if (t.startsWith("#")) return <span key={i} className="text-[hsl(var(--nb-comment))] italic">{t}</span>;
    if (/^["']/.test(t)) return <span key={i} className="text-[hsl(var(--nb-string))]">{t}</span>;
    if (KW.test(t) && /^[a-zA-Z]+$/.test(t)) return <span key={i} className="text-[hsl(var(--nb-keyword))] font-semibold">{t}</span>;
    if (/^\d/.test(t)) return <span key={i} className="text-[hsl(var(--nb-number))]">{t}</span>;
    return t;
  });

export const MarkdownCell = ({ children, testid }) => (
  <div data-testid={testid} className="nb-cell group grid grid-cols-[56px_1fr] sm:grid-cols-[84px_1fr] gap-2 py-2">
    <div />
    <div className="nb-md px-1">{children}</div>
  </div>
);

export const CodeCell = ({ n, code, children, running, onRun, testid, collapsible = false, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const count = running ? "*" : n ?? " ";
  return (
    <div data-testid={testid} className="nb-cell group py-2 fade-up">
      <div className="grid grid-cols-[56px_1fr] sm:grid-cols-[84px_1fr] gap-2">
        <div className="nb-prompt text-[hsl(var(--nb-in))] pt-2 flex items-start justify-end gap-1">
          {onRun && (
            <button data-testid={testid ? `${testid}-run` : undefined} onClick={onRun} disabled={running} title="Run cell" className="opacity-60 group-hover:opacity-100 hover:text-primary transition-opacity">
              {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            </button>
          )}
          <span className="hidden sm:inline">In&nbsp;[{count}]:</span><span className="sm:hidden">[{count}]</span>
        </div>
        <pre className="nb-input font-mono text-[12.5px] leading-relaxed whitespace-pre-wrap break-words">{highlight(code)}</pre>
      </div>
      {children && (
        <div className="grid grid-cols-[56px_1fr] sm:grid-cols-[84px_1fr] gap-2 mt-1">
          <div className="nb-prompt text-[hsl(var(--nb-out))] pt-1 flex justify-end">
            {collapsible ? (
              <button data-testid={testid ? `${testid}-toggle` : undefined} onClick={() => setOpen(!open)} className="flex items-center gap-0.5 hover:text-foreground">
                {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}<span className="hidden sm:inline">Out[{n}]:</span>
              </button>
            ) : n != null && <span className="hidden sm:inline">Out[{n}]:</span>}
          </div>
          <div className="min-w-0 py-1">{open ? children : <div className="text-xs font-mono text-muted-foreground">… output collapsed</div>}</div>
        </div>
      )}
    </div>
  );
};

export const Stream = ({ children, kind = "stdout", testid }) => (
  <pre data-testid={testid} className={`font-mono text-[12px] leading-relaxed whitespace-pre-wrap rounded px-3 py-2 ${kind === "stderr" ? "bg-[hsl(var(--nb-err-bg))] text-[hsl(var(--nb-err))]" : "text-foreground"}`}>{children}</pre>
);
